import { useMemo } from 'react';
import useDebounce from '@/hooks/useDebounce';
import {
  useGetLogisticOperatorsQuery,
  useGetLogisticOperatorByIdQuery,
} from '../services/logistic-operators.service';
import { useGetHeadquarterLogisticOperatorsQuery } from '@/pages/Business/pages/Shippings/services/headquarterLogisticOperators.service';
import { Option } from '@/components/inputs/AutocompleteInput';
import type { LogisticOperator } from '../models';
import { mapLogisticOperatorApiToModel } from '../adapters';
import {
  formatLogisticOperatorBusinessDisplayLabel,
  formatLogisticOperatorNameDisplayLabel,
} from '../utils/logisticOperatorSuggestionLabel';

const DEBOUNCE_MS = 350;
const LIST_PAGE_SIZE = 20;

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type LogisticOperatorAutocompleteSearchField = 'name' | 'businessName';

const isSubOperator = (operator: LogisticOperator): boolean =>
  !operator.headquarter || !!operator.parentId;

const getOperatorLabel = (
  operator: LogisticOperator,
  showSubSuffix: boolean,
  searchField: LogisticOperatorAutocompleteSearchField
): string => {
  const base =
    searchField === 'name'
      ? formatLogisticOperatorNameDisplayLabel(operator)
      : formatLogisticOperatorBusinessDisplayLabel(operator);
  if (showSubSuffix && isSubOperator(operator)) {
    return `${base} — (Sub)`;
  }
  return base;
};

const mapOperatorsToAutocompleteOptions = (
  operators: LogisticOperator[],
  showSubSuffix: boolean,
  searchField: LogisticOperatorAutocompleteSearchField
): Option[] =>
  operators.map((op) => ({
    label: getOperatorLabel(op, showSubSuffix, searchField),
    data: op,
  }));

interface UseLogisticOperatorAutocompleteProps {
  search?: string;
  minLength?: number;
  headquartersOnly?: boolean;
  searchField?: LogisticOperatorAutocompleteSearchField;
}

const useLogisticOperatorAutocomplete = ({
  search,
  minLength = 3,
  headquartersOnly = false,
  searchField = 'name',
}: UseLogisticOperatorAutocompleteProps = {}) => {
  const trimmedLive = (search ?? '').trim();
  const debouncedSearch = useDebounce(trimmedLive, DEBOUNCE_MS);
  const isSyncedWithInput = debouncedSearch === trimmedLive;

  const shouldFetchAllOperators = !!(
    debouncedSearch.length >= minLength && isSyncedWithInput
  );

  const listArgs =
    searchField === 'name'
      ? {
          paginated: { page: 1, pageSize: LIST_PAGE_SIZE },
          search: debouncedSearch,
        }
      : {
          paginated: { page: 1, pageSize: LIST_PAGE_SIZE },
          businessName: debouncedSearch,
        };

  const {
    data: allOperatorsResponse,
    isFetching: isAllFetching,
    error: allError,
  } = useGetLogisticOperatorsQuery(listArgs, {
    skip: headquartersOnly || !shouldFetchAllOperators,
  });

  const {
    data: headquarterOperatorsData,
    isFetching: isHeadquartersFetching,
    error: headquartersError,
  } = useGetHeadquarterLogisticOperatorsQuery(
    { paginated: { page: 1, pageSize: LIST_PAGE_SIZE } },
    { skip: !headquartersOnly || !shouldFetchAllOperators }
  );

  const isUuidShape = UUID_REGEX.test(debouncedSearch);
  const shouldFetchById =
    shouldFetchAllOperators && isUuidShape && !headquartersOnly;

  const { data: byIdOperator, isFetching: isByIdFetching } =
    useGetLogisticOperatorByIdQuery(
      { logisticOperatorId: debouncedSearch },
      { skip: !shouldFetchById }
    );

  const logisticOperatorOptions: Option[] = useMemo(() => {
    if (!shouldFetchAllOperators) return [];

    if (headquartersOnly && headquarterOperatorsData) {
      const operators = headquarterOperatorsData.map(
        mapLogisticOperatorApiToModel
      );
      const searchTerm = debouncedSearch.toLowerCase();
      const matchesSearchTerm = (op: LogisticOperator): boolean => {
        const idMatch = op.id.toLowerCase().includes(searchTerm);
        const nameMatch = op.fullName.toLowerCase().includes(searchTerm);
        const businessMatch =
          op.businessName?.toLowerCase().includes(searchTerm) ?? false;
        return idMatch || nameMatch || businessMatch;
      };
      const filtered = operators.filter(matchesSearchTerm);
      return mapOperatorsToAutocompleteOptions(
        filtered,
        false,
        searchField
      );
    }

    const fromList = allOperatorsResponse?.items ?? [];
    const merged: LogisticOperator[] = [];
    const seen = new Set<string>();

    if (byIdOperator) {
      merged.push(byIdOperator);
      seen.add(byIdOperator.id);
    }

    for (const op of fromList) {
      if (seen.has(op.id)) continue;
      seen.add(op.id);
      merged.push(op);
    }

    return mapOperatorsToAutocompleteOptions(merged, true, searchField);
  }, [
    allOperatorsResponse?.items,
    byIdOperator,
    debouncedSearch,
    headquarterOperatorsData,
    headquartersOnly,
    searchField,
    shouldFetchAllOperators,
  ]);

  const isLogisticOperatorsLoading =
    shouldFetchAllOperators &&
    (headquartersOnly
      ? isHeadquartersFetching
      : isAllFetching || (shouldFetchById && isByIdFetching));

  const logisticOperatorsError = headquartersOnly
    ? headquartersError
    : allError;

  return {
    logisticOperatorOptions,
    isLogisticOperatorsLoading,
    logisticOperatorsError,
  };
};

export default useLogisticOperatorAutocomplete;
